/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

// This is a controlled component. The parent must manage the toggle state via the `onToggle` and `value` props.
type Props = {
  value: boolean;
  onToggle: () => void;
  ariaLabel?: string;
  className?: string;
  offIcon: React.ReactElement | string;
  onIcon: React.ReactElement | string;
};

const styles = {
  outerContainer: () => css`
    padding: var(--cds-spacing-100);
  `,
  circleAroundSvg: (_: Theme, value: boolean) => css`
    border-radius: 50%;
    width: 21px;
    height: 21px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${value ? 'var(--cds-color-white-0)' : 'var(--cds-color-grey-300)'};
    position: relative;
    top: -50%;
  `,
  centerPill: (_: Theme, value: boolean) => css`
    width: 45px;
    border-radius: 8px;
    height: 10px;
    background-color: var(--cds-color-grey-500);
    display: flex;
    ${value && 'justify-content: flex-end;'}
  `,
};

const ToggleSwitchStandalone = (props: Props) => {
  const theme = useTheme();
  const { value } = props;

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const { onToggle } = props;

    onToggle();
  };

  const { ariaLabel, className, offIcon, onIcon } = props;

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      aria-pressed={value}
      className={className}
      css={styles.outerContainer}
    >
      <div css={styles.centerPill(theme, value)}>
        {!value && <div css={styles.circleAroundSvg(theme, false)}>{offIcon}</div>}
        {value && <div css={styles.circleAroundSvg(theme, true)}>{onIcon}</div>}
      </div>
    </button>
  );
};

export default ToggleSwitchStandalone;
